import React, { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { IDropDownOption } from "@/types/DropDown";
import { IFolderTreeOptions } from "@/components/files/folderTree";
import { GroupBase, SelectInstance, StylesConfig } from "react-select";
import { useDispatch } from "react-redux";
import styles from "@/components/forms/inputs/dropDownAsync.module.css";
import { useTranslation } from "react-i18next";
import {
  filterPreloadedDropDownOptions,
  useDropDownOptionPreload,
} from "@/helper/customHooks/useDropDownOptionPreload";
import DropDownOptionPreloadRow from "@/components/forms/inputs/dropDownOptionPreloadRow";

interface Props {
  options: IDropDownOption[] | IFolderTreeOptions[];
  preExistingValue?: any;
  isMulti?: any;
  dataCallback: any;
  onChange?: (
    values:
      | IDropDownOption
      | IFolderTreeOptions
      | IDropDownOption[]
      | IFolderTreeOptions[],
    label?: any,
  ) => void;
  labelStyle?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  wrapperClassName?: string;
  labelClassName?: string;
  mandatory?: boolean;
  label?: string;
  labelAdditionalContent?: React.ReactNode;
  onBlur?: any;
  onFocus?: any;
  isClearable?: boolean;
  isSearchable?: boolean;
  disabled?: boolean;
  name?: string;
  required?: boolean;
  deleteItem?: boolean;
  newTemplateCreated?: boolean;
  editedItem?: any;
  error?: any;
  resetInput?: any;
  autofocus?: boolean;
  placeholder?: React.ReactNode;
  formatOptionLabel?: any;
  isOptionDisabled?: (option: any) => boolean;
  useGlobalstate?: boolean;
  customStyles?: StylesConfig<any, any, GroupBase<any>> | undefined;
  alignHeight?: boolean;
  defaultOptions?: boolean | IDropDownOption[];
  showLoadingIndicator?: boolean;
  loadAllOptions?: () => Promise<IDropDownOption[]>;
  loadAllOptionsResetKey?: unknown;
  onLoadAllOptionsError?: (error: unknown) => void;
}

export const DropDownAsync = React.forwardRef<SelectInstance, Props>(
  function DropDownAsync(props, ref: any) {
    const [values, setValues] = useState<
      | IDropDownOption[]
      | IFolderTreeOptions[]
      | IDropDownOption
      | IFolderTreeOptions
      | any
    >([]);
    const dispatch = useDispatch();
    const selectRef = useRef<SelectInstance | null>(null);
    const { t } = useTranslation();
    const {
      defaultOptions,
      forceMenuOpen,
      handlePreload,
      hasPreloadedOptions,
      isPreloading,
      preloadedOptions,
      onMenuClose,
      openMenu,
    } = useDropDownOptionPreload<IDropDownOption>({
      disabled: props.disabled,
      loadAllOptions: props.loadAllOptions,
      resetKey: props.loadAllOptionsResetKey,
      fallbackDefaultOptions: props.defaultOptions,
      onLoadError: props.onLoadAllOptionsError,
    });

    const debounce = (fn: any, delay = 250) => {
      let timeout: any;

      return (...args: any) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          fn(...args);
        }, delay);
      };
    };

    useEffect(() => {
      if (!props.autofocus) {
        return;
      }

      const frameId = requestAnimationFrame(() => {
        selectRef.current?.focus?.();
      });

      return () => cancelAnimationFrame(frameId);
    }, [props.autofocus]);

    useEffect(() => {
      if (
        props.preExistingValue !== undefined &&
        props.preExistingValue !== null &&
        Object.keys(props.preExistingValue).length !== 0
      ) {
        setValues(props.preExistingValue);
      } else if (props.preExistingValue === null) {
        setValues([]);
      }
    }, [props.preExistingValue]);

    const onChangeHandler = (selectedOption: any, action?: any) => {
      setValues(selectedOption);
      props.onChange?.(selectedOption, props.name);

      if (action.action === "clear" && props.resetInput) {
        props.resetInput();
      }
      if (action.action === "clear" && props.useGlobalstate) {
        dispatch({ type: "clearSubjectSearch" });
      }
    };

    const loadOptionsDebounced = debounce(
      (inputValue: string, callback: any) => {
        props.dataCallback(inputValue, callback);
      },
      500,
    );

    const handleLoadOptions = (
      inputValue: string,
      callback: (options: IDropDownOption[]) => void,
    ) => {
      if (preloadedOptions !== null) {
        callback(filterPreloadedDropDownOptions(preloadedOptions, inputValue));
        return;
      }
      loadOptionsDebounced(inputValue, callback);
    };

    const setCombinedRef = (instance: SelectInstance | null) => {
      selectRef.current = instance;

      if (typeof ref === "function") {
        ref(instance);
      } else if (ref && typeof ref === "object") {
        ref.current = instance;
      }
    };

    const handlePreloadClick = async () => {
      selectRef.current?.focus?.();
      const shouldOpenMenu = await handlePreload();
      const select = selectRef.current;

      if (shouldOpenMenu && select?.inputRef === document.activeElement) {
        openMenu();
      }
    };

    return (
      <div
        className={props.wrapperClassName}
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          ...props.wrapperStyle,
        }}
      >
        {props.label && (
          <label
            htmlFor={props.name}
            className={props.labelClassName}
            style={props.labelStyle}
          >
            {props.mandatory === true ? (
              <div className={styles.labelWrapper}>
                <span>
                  {props.label}
                  <span style={{ color: "red" }}>*</span>
                </span>
                {props.labelAdditionalContent}
              </div>
            ) : (
              <div className={styles.labelWrapper}>
                {props.label}
                {props.labelAdditionalContent}
              </div>
            )}
          </label>
        )}
        <DropDownOptionPreloadRow
          disabled={props.disabled}
          hasLoadedOptions={hasPreloadedOptions}
          isLoading={isPreloading}
          onBlur={onMenuClose}
          onPreload={props.loadAllOptions ? handlePreloadClick : undefined}
        >
          <AsyncSelect
            autoFocus={props.autofocus}
            ref={setCombinedRef}
            inputId={props.name}
            aria-label={props.name ? undefined : "DropDown-Async-Select"}
            loadOptions={handleLoadOptions}
            defaultOptions={defaultOptions}
            isLoading={isPreloading || props.showLoadingIndicator || undefined}
            menuIsOpen={hasPreloadedOptions ? forceMenuOpen : undefined}
            onMenuClose={onMenuClose}
            onMenuOpen={hasPreloadedOptions ? openMenu : undefined}
            loadingMessage={() => t("loading")}
            noOptionsMessage={({ inputValue }) =>
              inputValue.trim().length > 0
                ? t("no_options_found")
                : t("type_to_search_placeholder")
            }
            value={values}
            onChange={(selectedOption, action) =>
              onChangeHandler(selectedOption, action)
            }
            onBlur={props.onBlur}
            name={props.name}
            isMulti={props.isMulti}
            isDisabled={props.disabled}
            isSearchable={props.isSearchable}
            isClearable={props.isClearable}
            required={props.required}
            placeholder={props.placeholder ?? t("search_placeholder")}
            onFocus={props.onFocus}
            menuPortalTarget={document.body}
            formatOptionLabel={props.formatOptionLabel}
            isOptionDisabled={props.isOptionDisabled}
            styles={{
              clearIndicator: (base) => ({
                ...base,
                color: "#ba78de",
                backgroundColor: "#f4f2f2",
                borderRadius: "0.75rem",
                padding: "4px",
                marginRight: "0.3rem",
                "&:hover": {
                  color: "#8c1ec8",
                },
              }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: props.error ? "#d32f2f" : "",
              }),
              input: (baseStyles) => ({
                ...baseStyles,
                margin: "0",
                padding: "0",
              }),
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "0.75rem",
                borderColor: props.error ? "#d32f2f" : "rgba(0, 0, 0, 0.23)",
                ...(props.alignHeight && {
                  height: "3rem",
                  minHeight: "3rem",
                }),
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
                padding: "0.75rem",
                borderRadius: "0.75rem",
                ...(props.alignHeight && {
                  padding: "0 0.75rem",
                  height: "3rem",
                }),
              }),
              dropdownIndicator: (base) => ({
                ...base,
                color: "#ba78de",
                backgroundColor: "#f4f2f2",
                borderRadius: "0.75rem",
                padding: "4px",
                margin: "0 0.3rem",
                ...(props.alignHeight && {
                  height: "2.3rem",
                }),
                "&:hover": {
                  color: "#8c1ec8",
                },
              }),
              loadingIndicator: (base) => ({
                ...base,
                color: "#ba78de",
              }),
              menuPortal: (base) => ({
                ...base,
                borderRadius: "0.75rem",
                zIndex: 99999,
              }),
              option: (styles, { isDisabled, isFocused, isSelected }) => {
                return {
                  ...styles,
                  backgroundColor: isDisabled
                    ? undefined
                    : isSelected
                      ? "#f4f2f2"
                      : isFocused
                        ? "#a34bd3"
                        : undefined,
                  color: isDisabled
                    ? undefined
                    : isSelected
                      ? "black"
                      : isFocused
                        ? "white"
                        : undefined,
                  cursor: isDisabled ? "not-allowed" : "default",
                };
              },
              menu: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "0.75rem",
              }),
              multiValue: (baseStyles) => ({
                ...baseStyles,
                backgroundColor: "#8c1ec8",
                borderRadius: "0.75rem",
                color: "white",
                padding: "0.25rem 0.5rem 0.25rem 0.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.25rem",
              }),
              multiValueLabel: (baseStyles) => ({
                ...baseStyles,
                color: "white",
              }),
              multiValueRemove: (baseStyles) => ({
                ...baseStyles,
                borderRadius: "0.75rem",
                color: "#8c1ec8",
                backgroundColor: "white",
                padding: "0.025rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ":hover": {
                  color: "#ff1744",
                },
              }),
              ...props.customStyles,
            }}
          />
        </DropDownOptionPreloadRow>
      </div>
    );
  },
);
